import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime } from '../../helpers/formatDateTime';
import { ActionList, Modal, StatusMessage } from '../ui';
import { ModalContent } from '../ui/ModalContent';
import { deleteVirtualTenant } from '../../services/siteManagement/siteManagement.service';
import type { VirtualTenantDetail } from '../../services/siteManagement/siteManagement.types';
import { useTreeRefresh } from '../../contexts/TreeRefreshContext';

interface VirtualTenantsListProps {
  items: VirtualTenantDetail[];
  onItemsChanged?: (items: VirtualTenantDetail[]) => void;
  onError?: (message: string) => void;
  onDeleted?: () => void;
  onEdit?: (item: VirtualTenantDetail) => void;
  siteId?: string | number;
  createdMessage?: string | null;
  onClearCreatedMessage?: () => void;
}

interface EditingVT {
  id: number;
  name: string;
}

const VirtualTenantsList: React.FC<VirtualTenantsListProps> = ({ items, onItemsChanged, onError, onDeleted, onEdit, createdMessage, onClearCreatedMessage }) => {
  const [actionInFlight, setActionInFlight] = useState<number | null>(null);
  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const [modalHeadingText, setModalHeadingText] = useState<string | undefined>(undefined);
  const [modalMessageText, setModalMessageText] = useState<string | undefined>(undefined);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);
  const [pendingDeleteItem, setPendingDeleteItem] = useState<VirtualTenantDetail | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { refreshTree } = useTreeRefresh();

  const handleEdit = (item: VirtualTenantDetail) => {
    if (onEdit) {
      onEdit(item);
      setSuccessMessage(null);
      setErrorMessage(null);
    }
  };

  const waitForModalConfirm = (): Promise<boolean> => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setOpenModalConfirm(true);
    });
  };

  const handleDelete = async (item: VirtualTenantDetail) => {
    setSuccessMessage(null);
    setErrorMessage(null);
    setModalHeadingText('Delete Virtual Tenant');
    setModalMessageText(`Are you sure you want to delete virtual tenant "${item.name}"?`);
    setPendingDeleteItem(item);
    const confirmed = await waitForModalConfirm();
    if (!confirmed) {
      setPendingDeleteItem(null);
      return;
    }
    setActionInFlight(item.id);
    try {
      const res = await deleteVirtualTenant(item.id);
      if (res?.success) {
        onItemsChanged?.(items.filter(i => i.id !== item.id));
        await refreshTree();
        setSuccessMessage('Virtual tenant deleted successfully');
        await new Promise(resolve => setTimeout(resolve, 200));
        onDeleted?.();
      } else {
        setErrorMessage(res?.error?.message || 'Failed to delete virtual tenant');
        onError?.(res?.error?.message || 'Failed to delete virtual tenant');
      }
    } catch (e: any) {
      setErrorMessage(e?.message || 'Error deleting virtual tenant');
      onError?.(e?.message || 'Error deleting virtual tenant');
    } finally {
      setActionInFlight(null);
      setPendingDeleteItem(null);
    }
  };

  if (!items || items.length === 0) {
    return <>
      {successMessage && <StatusMessage kind="success">{successMessage}</StatusMessage>}
      {errorMessage && <StatusMessage kind="error">{errorMessage}</StatusMessage>}
      <p>No virtual tenants yet.</p>
    </>;
  }

  return (
    <>
      {(createdMessage || successMessage) && (
        <StatusMessage kind="success" onClose={() => {
          setSuccessMessage(null);
          onClearCreatedMessage?.();
        }}>
          {createdMessage || successMessage}
        </StatusMessage>
      )}
      {errorMessage && <StatusMessage kind="error">{errorMessage}</StatusMessage>}
      <Modal
        isOpen={openModalConfirm}
        title={modalHeadingText ?? ''}
        headerClass="modal__header--danger"
        content={<ModalContent heading={modalHeadingText} message={modalMessageText} />}
        onConfirm={() => {
          resolverRef.current?.(true);
          setOpenModalConfirm(false);
        }}
        onCancel={() => {
          resolverRef.current?.(false);
          setOpenModalConfirm(false);
        }}
      />
      <table className="table table--compact">
      <thead>
        <tr>
          <th>Name</th>
          <th>Alarm Paths</th>
          <th>Rules</th>
          <th>Created</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {items.map(item => (
          <tr key={item.id}>
            <td>
              <Link to={`/virtualTenants/vt/${item.id}`} className="alarm-name-link">
                {item.name}
              </Link>
            </td>
            <td>{(item.associatedAlarmPaths?.length || 0)}</td>
            <td>
              {(item as any).inheritedRules && (item as any).inheritedRules.length > 0 ? (
                <span title="Inherited from parent site">
                  {(item as any).inheritedRules.map((r: any) => r.ruleName).join(', ')}
                  <span style={{ color: '#888', fontSize: '0.9em' }}> (inherited)</span>
                </span>
              ) : (
                <span>{item.ruleCount || 0} rules</span>
              )}
            </td>
            <td>{formatDateTime(item.createdAt)}</td>
            <td>
              <ActionList
                onEdit={() => handleEdit(item)}
                onDelete={() => handleDelete(item)}
                disabled={actionInFlight !== null}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    </>);
};

export default VirtualTenantsList;
